import { Link } from "@tanstack/react-router";
import type { OverviewConcern } from "@rua-watch/shared";
import { fromUnix } from "../lib/format";

// Ranked list of issues across the monitored portfolio. Backend already
// sorts by severity then recency and caps at 10 — UI just renders.
// Empty state is informative: nothing to worry about right now.

export function TopConcerns({ concerns }: { concerns: OverviewConcern[] }) {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-4 py-2">
        <h2 className="text-sm font-medium text-slate-700 dark:text-slate-300">Top concerns</h2>
        <span className="text-xs text-slate-500 dark:text-slate-400">{concerns.length}</span>
      </div>
      {concerns.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
          Nothing to act on. Portfolio looks healthy.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {concerns.map((c, i) => (
            <ConcernRow key={`${c.kind}-${c.domain ?? "global"}-${i}`} concern={c} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ConcernRow({ concern }: { concern: OverviewConcern }) {
  const sevColor = {
    critical: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    warning:  "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    info:     "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
  }[concern.severity];
  const kindLabel = {
    domain_critical:    "Critical",
    domain_warning:     "Warning",
    alert_recent:       "Alert",
    dns_change_recent:  "DNS",
    ip_blocklisted:     "Listed",
  }[concern.kind];

  return (
    <li>
      <Link
        to={concern.href as never}
        className="flex items-start gap-3 px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60"
      >
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${sevColor}`}>
          {kindLabel}
        </span>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="truncate text-sm text-slate-800 dark:text-slate-200">{concern.summary}</p>
          {concern.detail ? (
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{concern.detail}</p>
          ) : null}
        </div>
        {concern.timestamp ? (
          <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
            {fromUnix(concern.timestamp)}
          </span>
        ) : null}
      </Link>
    </li>
  );
}
